/**
 * `<FormuleWeb>` — UNE formule d'une fiche, dessinée en SVG du DOM (chantier
 * « manuel vivant », Φ-MV-2, MV-SAV-04).
 *
 * ─── Le moteur est celui de l'application, pas un second ────────────────────
 *
 * `DECISIONS-ARCHITECTE.md`, point 13 : « formules de la console : le moteur
 * de l'application, partagé ». La mise en page est `disposerMath`
 * (`@silex/shared`) — la même fonction pure que `Formule.tsx` côté
 * application. Ce fichier ne fait que PEINDRE sa liste d'affichage : il ne
 * décide d'aucune position. La console ne peut donc pas rendre une écriture
 * que l'application refuserait, ni la rendre autrement. Ni KaTeX, ni CDN.
 *
 * La prose d'une fiche (une phrase où une formule se glisse) passe, elle, par
 * `TexteMaths` (MathML), comme dans tout le reste de la console. Ici, ce sont
 * les écritures POSÉES : la ligne d'une démarche, l'écriture d'un piège, une
 * loi — celles dont une annotation peut viser un morceau.
 *
 * ─── Aucune couleur littérale ───────────────────────────────────────────────
 *
 * Chaque trace reçoit une CLASSE de rôle (`fiche-encre--accent`…), résolue par
 * les variables de `theme.css`.
 */
import {
  analyserTexteLatex,
  decrireMath,
  disposerMath,
  replierProse,
  soulignerCible,
  type BoiteCible,
  type BoiteMath,
  type NoeudMath,
} from '@silex/shared';
import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { classeDuGlyphe } from '../../lib/police-maths';

export interface FormuleAnalysee {
  readonly noeud: NoeudMath;
  readonly boite: BoiteMath;
}

/** Lit et met en page une formule seule. `null` : hors de la liste fermée. */
export function analyserFormule(tex: string): FormuleAnalysee | null {
  const segments = analyserTexteLatex(`\\(${tex}\\)`);
  const seul = segments.length === 1 ? segments[0] : undefined;
  if (seul === undefined || seul.type !== 'expression') return null;
  const boite = disposerMath(seul.noeud);
  return boite === null ? null : { noeud: seul.noeud, boite };
}

/** Les boîtes des cibles d'une formule, en pixels, depuis son coin haut gauche. */
export function ciblesEnPixels(boite: BoiteMath, taille: number): ReadonlyMap<string, BoiteCible> {
  const sortie = new Map<string, BoiteCible>();
  for (const d of boite.dessins) {
    if (d.type !== 'repere') continue;
    sortie.set(d.nom, {
      g: d.x * taille,
      d: (d.x + d.largeur) * taille,
      h: (d.y + boite.montee) * taille,
      b: (d.y + d.hauteur + boite.montee) * taille,
    });
  }
  return sortie;
}

/** La taille à laquelle la formule tient dans `largeurMax`. */
export function tailleAjustee(boite: BoiteMath, taille: number, largeurMax?: number): number {
  if (largeurMax === undefined || largeurMax <= 0) return taille;
  const largeur = boite.largeur * taille;
  return largeur <= largeurMax ? taille : (taille * largeurMax) / largeur;
}

/** L'encre d'une écriture — un RÔLE du format, jamais une couleur. */
export type TonFormule = 'encre' | 'accent' | 'doux' | 'ardoise';

/**
 * Les dessins d'une boîte, en PIXELS, décalés de (`dx`, `dy`) — `dy` est la
 * ligne de base. À poser dans un `<svg>` : c'est aussi ce qu'un dessin du
 * registre emploie pour ses formules.
 */
export function DessinsFormule({
  boite,
  taille,
  dx = 0,
  dy = 0,
  ton,
}: {
  readonly boite: BoiteMath;
  readonly taille: number;
  readonly dx?: number;
  readonly dy?: number;
  readonly ton: TonFormule;
}): ReactNode {
  const plein = `fiche-encre fiche-encre--${ton}`;
  /* Un opérateur garde l'encre de la formule quand elle porte un rôle : une
     écriture « à revoir » est ardoise d'un bout à l'autre. */
  const operateur = ton === 'encre' ? 'fiche-encre fiche-encre--doux' : plein;
  return (
    <>
      {boite.dessins.map((d, i) => {
        if (d.type === 'repere') return null;
        if (d.type === 'glyphe') {
          return (
            <text
              key={i}
              x={dx + d.x * taille}
              y={dy + d.y * taille}
              className={`${classeDuGlyphe(d.police)} ${d.role === 'operateur' ? operateur : plein}`}
              fontSize={d.taille * taille}
              textAnchor="middle"
            >
              {d.texte}
            </text>
          );
        }
        if (d.type === 'filet') {
          return (
            <rect
              key={i}
              x={dx + d.x * taille}
              y={dy + (d.y - d.epaisseur / 2) * taille}
              width={d.largeur * taille}
              height={d.epaisseur * taille}
              className={plein}
            />
          );
        }
        if (d.type === 'barre') {
          return (
            <rect
              key={i}
              x={dx + (d.x - d.epaisseur / 2) * taille}
              y={dy + d.yHaut * taille}
              width={d.epaisseur * taille}
              height={(d.yBas - d.yHaut) * taille}
              className={plein}
            />
          );
        }
        return (
          <path
            key={i}
            d={d.d}
            transform={`translate(${dx} ${dy}) scale(${taille})`}
            className={`fiche-trace fiche-trace--${ton}`}
            strokeWidth={d.epaisseur}
          />
        );
      })}
    </>
  );
}

export interface FormuleWebProps {
  readonly tex: string;
  /** Corps, en pixels. Défaut : 19. */
  readonly taille?: number;
  readonly ton?: 'encre' | 'accent' | 'ardoise';
  /** La formule est réduite pour tenir dans cette largeur — jamais de défilement horizontal. */
  readonly largeurMax?: number;
  /** Les cibles à souligner d'un trait de plume (mise en page empilée). */
  readonly soulignees?: readonly string[];
}

/** Place laissée sous la formule pour un soulignement. */
const MARGE_SOULIGNE = 7;

export function FormuleWeb({
  tex,
  taille = 19,
  ton = 'encre',
  largeurMax,
  soulignees,
}: FormuleWebProps): ReactNode {
  const analysee = useMemo(() => analyserFormule(tex), [tex]);

  if (analysee === null) {
    /* Hors de la liste fermée : jamais de source LaTeX à l'écran. Le
       validateur refuse ce cas à l'import ; ceci n'est que la ceinture. */
    return (
      <span className="fiche-formule fiche-formule--repliee">{replierProse(`\\(${tex}\\)`)}</span>
    );
  }

  const { boite, noeud } = analysee;
  const t = tailleAjustee(boite, taille, largeurMax);
  const largeur = boite.largeur * t;
  const hauteur = (boite.montee + boite.descente) * t;
  const cibles = soulignees && soulignees.length > 0 ? ciblesEnPixels(boite, t) : null;
  const bas = cibles === null ? 0 : MARGE_SOULIGNE;

  return (
    <svg
      className="fiche-formule"
      width={largeur}
      height={hauteur + bas}
      viewBox={`0 0 ${largeur} ${hauteur + bas}`}
      role="img"
      aria-label={decrireMath(noeud)}
    >
      <DessinsFormule boite={boite} taille={t} dy={boite.montee * t} ton={ton} />
      {cibles !== null &&
        soulignees?.map((nom, rang) => {
          const cible = cibles.get(nom);
          if (cible === undefined) return null;
          return (
            <path
              key={nom}
              d={soulignerCible(cible, rang)}
              className="fiche-plume fiche-plume--marque"
            />
          );
        })}
    </svg>
  );
}

/**
 * Une formule qui prend la largeur qu'on lui laisse, et se réduit pour y
 * tenir. La largeur est celle de son conteneur, lue après la mise en page.
 */
export function FormuleAjustee(props: Omit<FormuleWebProps, 'largeurMax'>): ReactNode {
  const conteneur = useRef<HTMLSpanElement>(null);
  const [largeur, setLargeur] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    const el = conteneur.current;
    if (el === null) return undefined;
    const lire = (): void => {
      const l = Math.floor(el.clientWidth);
      setLargeur((avant) => (avant === l ? avant : l));
    };
    lire();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observateur = new ResizeObserver(lire);
    observateur.observe(el);
    return () => observateur.disconnect();
  }, []);
  return (
    <span ref={conteneur} className="fiche-formule-ajustee">
      <FormuleWeb {...props} largeurMax={largeur} />
    </span>
  );
}
